import React, { useEffect, useState, useRef } from "react";
import { getRedesSociales, createRedSocial, updateRedSocial, deleteRedSocial } from "../api/redesSociales.js";
import { getRestaurantes } from "../api/restaurantes.js";
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Globe } from "lucide-react";

const RedesSocialesPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [redesSociales, setRedesSociales] = useState([]);
    const [redSocial, setRedSocial] = useState({
        tipo: "",
        url: "",
        id_restaurante: "",
    });
    const [filteredRedes, setFilteredRedes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [restaurantes, setRestaurantes] = useState([]);
    const inputRefs = useRef({});
    const { sortedItems: sortedRedes, sortConfig, setSortConfig } = useSortableData(filteredRedes);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentRedes, totalPages, itemsPerPage } = usePagination(sortedRedes, 20);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // Cargar restaurantes
    useEffect(() => {
        const loadRestaurantes = async () => {
            try {
                const data = await getRestaurantes();
                setRestaurantes(Array.isArray(data) ? data : data.restaurantes || []);
            } catch (err) {
                console.error("Error al cargar restaurantes:", err);
            }
        };
        loadRestaurantes();
    }, []);

    // Cargar redes sociales
    const loadRedesSociales = async () => {
        try {
            const data = await getRedesSociales();
            const lista = Array.isArray(data) ? data : data.redesSociales || [];
            setRedesSociales(lista);
            setFilteredRedes(lista);
        } catch (err) {
            console.error("Error al cargar redes sociales:", err);
        }
    };

    useEffect(() => {
        loadRedesSociales();
    }, []);

    // Filtro
    useEffect(() => {
        const filtered = redesSociales.filter((r) =>
            Object.values(r).some((val) =>
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setFilteredRedes(filtered);
    }, [searchTerm, redesSociales]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setRedSocial({ ...redSocial, [name]: value });
    };

    const resetForm = () => {
        setRedSocial({ 
            tipo: "",
            url: "",
            id_restaurante: usuarioLogueado?.isSuperAdmin ? "" : usuarioLogueado?.id_restaurante || "" 
        });
        setErrors({});
        setEditMode(false);
    };

    const validateRedSocial = (data) => {
        const newErrors = {};
        if (!data.tipo || data.tipo.trim() === "")
            newErrors.tipo = "Seleccione un tipo de red social.";
        if (!data.url || data.url.trim() === "")
            newErrors.url = "Ingrese una URL.";
        else {
            try {
                new URL(data.url);
            } catch (error) {
                newErrors.url = "La URL no tiene un formato válido.";
            }
        }
        if (!data.id_restaurante)
            newErrors.id_restaurante = "Seleccione un restaurante.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateRedSocial(redSocial);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstErrorField = Object.keys(validationErrors)[0];
            const fieldRef = inputRefs.current[firstErrorField];
            if (fieldRef?.scrollIntoView) {
                fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
                fieldRef.focus();
            }
            return;
        }

        try {
            const redSocialData = { ...redSocial };
            
            // Si no es SuperAdmin, asignar automáticamente el id_restaurante
            if (!usuarioLogueado?.isSuperAdmin) {
                redSocialData.id_restaurante = usuarioLogueado?.id_restaurante;
            }
            
            if (editMode) {
                await updateRedSocial(redSocial.id_red, redSocialData);
                alert("Red social actualizada correctamente");
            } else {
                await createRedSocial(redSocialData);
                alert("Red social creada correctamente");
            }
            resetForm();
            setModalOpen(false);
            loadRedesSociales();
        } catch (err) {
            console.error("Error del backend:", err.response?.data || err.message);
            const backendMsg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Error al guardar la red social.";
            setErrors({ general: backendMsg });
            alert(backendMsg);
        }
    };

    const handleEdit = (r) => {
        setRedSocial(r);
        setEditMode(true);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar esta red social?")) {
            await deleteRedSocial(id);
            loadRedesSociales();
        }
    };

    // Función para obtener el icono según el tipo
    const getIcono = (tipo) => {
        const iconProps = { size: 20, className: "inline" };
        switch (tipo.toLowerCase()) {
            case "facebook":
                return <Facebook {...iconProps} />;
            case "instagram":
                return <Instagram {...iconProps} />;
            case "twitter":
            case "x":
                return <Twitter {...iconProps} />;
            case "linkedin":
                return <Linkedin {...iconProps} />;
            case "youtube":
                return <Youtube {...iconProps} />;
            default:
                return <Globe {...iconProps} />;
        }
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
            <h1 className="text-3xl font-bold mb-6 text-center">Gestión de Redes Sociales</h1>

            {/* Buscador + botón */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
                <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nueva Red Social </button>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
                <table className="min-w-full text-sm text-left divide-y divide-gray-200">
                    <thead className="bg-gray-100 text-gray-700 font-semibold">
                        <tr>
                            <SortableHeader label="N°" sortKey="id_red" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <SortableHeader label="Tipo" sortKey="tipo" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <th className="px-4 py-2">URL</th>
                            {usuarioLogueado?.isSuperAdmin && (
                                <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            )}
                            <th className="px-4 py-2 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {currentRedes.length > 0 ? (
                            currentRedes.map((r, idx) => (
                                <tr key={r.id_red} className="hover:bg-gray-50">
                                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                    <td className="px-4 py-2">
                                        <span className="flex items-center gap-2">
                                            {getIcono(r.tipo)}
                                            <span className="capitalize">{r.tipo}</span>
                                        </span>
                                    </td>
                                    <td className="px-4 py-2">
                                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                            {r.url}
                                        </a>
                                    </td>
                                    {usuarioLogueado?.isSuperAdmin && (
                                        <td className="px-4 py-2">
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                                {r.restaurante?.nombre_comercial || "Sin restaurante"}
                                            </span>
                                        </td>
                                    )}
                                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                                        <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(r)} > Editar </button>
                                        <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(r.id_red)} > Eliminar </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={usuarioLogueado?.isSuperAdmin ? "5" : "4"} className="text-center py-4 text-gray-500"> No hay redes sociales registradas </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

            {/* Modal Crear / Editar */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">
                        <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl"> ✖ </button>
                        <h2 className="text-2xl font-semibold mb-6 text-center"> {editMode ? "Editar Red Social" : "Nueva Red Social"} </h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InputGroup label="Tipo de Red Social" error={errors.tipo}>
                                    <select name="tipo" value={redSocial.tipo} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["tipo"] = el)}>
                                        <option value="">Seleccione un tipo</option>
                                        <option value="Facebook">Facebook</option>
                                        <option value="Instagram">Instagram</option>
                                        <option value="Twitter">Twitter / X</option>
                                        <option value="LinkedIn">LinkedIn</option>
                                        <option value="YouTube">YouTube</option>
                                        <option value="TikTok">TikTok</option>
                                        <option value="WhatsApp">WhatsApp</option>
                                        <option value="Otro">Otro</option>
                                    </select>
                                </InputGroup>

                                {/* Campo Restaurante - Solo visible para SuperAdmin */}
                                {usuarioLogueado?.isSuperAdmin && (
                                    <InputGroup label="Restaurante" error={errors.id_restaurante}>
                                        <select name="id_restaurante" value={redSocial.id_restaurante} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["id_restaurante"] = el)}>
                                            <option value="">Seleccione un restaurante</option>
                                            {restaurantes.map((r) => (
                                                <option key={r.id_restaurante} value={r.id_restaurante}>
                                                    {r.nombre_comercial}
                                                </option>
                                            ))}
                                        </select>
                                    </InputGroup>
                                )}
                            </div>

                            <InputGroup label="URL" error={errors.url}>
                                <input 
                                    type="url" 
                                    name="url" 
                                    value={redSocial.url} 
                                    onChange={handleChange} 
                                    placeholder="https://facebook.com/tu-restaurante" 
                                    className="input-base" 
                                    ref={(el) => (inputRefs.current["url"] = el)}
                                />
                            </InputGroup>

                            {errors.general && ( <p className="text-red-600 text-sm text-center">{errors.general}</p> )}

                            <div className="text-center">
                                <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"> {editMode ? "Actualizar" : "Guardar"} </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RedesSocialesPage;
